/**
 * PromoteRackDialog — pasar un rack en borrador a producción.
 *
 * Se usa desde el listado de racks y desde el visor gráfico. Pide lo único que
 * el borrador no tiene: ubicación, nombre definitivo y (opcional) código. Si el
 * código se deja en blanco, lo genera la nomenclatura en el backend, que es
 * también el único sitio donde se consume el contador de numeración.
 *
 * NO valida los elementos: eso se hace después con el checklist de montaje,
 * cuando el agente ya ha rellenado serie/IP/MAC de los equipos.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Dialog from './common/Dialog';

export default function PromoteRackDialog({ rack, companyId, onClose, onPromoted }) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const [form, setForm] = useState({
    location_id: rack?.location_id ? String(rack.location_id) : '',
    code: '',
    // Si el nombre era el provisional del alta («Borrador 3F2A»), se deja en
    // blanco para que el usuario ponga el definitivo.
    name: /^Borrador\b/i.test(rack?.name || '') ? '' : (rack?.name || ''),
  });
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    api.getLocations('', companyId)
      .then((resp) => { if (!cancelled) setLocations(resp.data || []); })
      .catch(() => { if (!cancelled) setLocations([]); });
    return () => { cancelled = true; };
  }, [companyId]);

  const handleSubmit = async () => {
    if (!form.location_id) {
      setError(tr('rack.promote_location_required', 'Indica la ubicación del rack'));
      return;
    }
    if (!form.name?.trim()) {
      setError(tr('rack.promote_name_required', 'Indica el nombre definitivo del rack'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = await api.promoteRack(
        rack.id,
        {
          location_id: Number(form.location_id),
          name: form.name.trim(),
          code: form.code?.trim() || undefined,
        },
        companyId,
      );
      onPromoted?.(payload);
    } catch (err) {
      setError(err.message || tr('rack.promote_error', 'No se pudo pasar a producción'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={tr('rack.promote_title', 'Pasar a producción')}
      icon="🚀"
      maxWidth={560}
      onClose={busy ? undefined : onClose}
      footer={(
        <>
          <button type="button" className="list-btn-secondary" onClick={onClose} disabled={busy}>
            {tr('action.cancel', 'Cancelar')}
          </button>
          <button type="button" className="list-btn-primary" onClick={handleSubmit} disabled={busy}>
            {busy
              ? tr('msg.saving', 'Guardando…')
              : tr('rack.promote_confirm', 'Pasar a producción')}
          </button>
        </>
      )}
    >
      <p className="list-hint" style={{ marginBottom: 12 }}>
        {tr(
          'rack.promote_hint',
          'El armario y sus equipos dejan de ser un boceto y pasan a contar en el inventario. Se le asigna un código real (consume numeración) y debe tener ubicación. El montaje se valida después, con el checklist.',
        )}
      </p>

      <div className="mb-3">
        <label className="form-label">
          {tr('rack.location', 'Ubicación')} <span className="text-danger">*</span>
        </label>
        <select
          className="form-select"
          value={form.location_id}
          onChange={(e) => setForm((prev) => ({ ...prev, location_id: e.target.value }))}
          disabled={busy}
        >
          <option value="">— Selecciona ubicación —</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>{loc.name || loc.code}</option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label">
          {tr('rack.name', 'Nombre')} <span className="text-danger">*</span>
        </label>
        <input
          className="form-control"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          disabled={busy}
        />
      </div>

      <div className="mb-2">
        <label className="form-label">{tr('rack.code', 'Código')}</label>
        <input
          className="form-control"
          value={form.code}
          onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
          placeholder={tr('rack.code_auto', 'En blanco: lo genera la nomenclatura')}
          disabled={busy}
        />
      </div>

      {error && (
        <div className="alert alert-danger py-2 mt-3" role="alert">{error}</div>
      )}
    </Dialog>
  );
}
